import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getAdminCompensation, saveAdminCompensation } from "../../service/api";
import { Card, Pill, fmtDate, fmtMoney, isSuperAdmin, Forbidden, Loading } from "./ui";

export default function SalarySettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [savedAt, setSavedAt] = useState({});
  const [edits, setEdits] = useState({});

  // Initial load only. Saves update the affected row in place, so the table never unmounts.
  useEffect(() => {
    let alive = true;
    getAdminCompensation()
      .then((res) => { if (alive) setRows(res?.data || []); })
      .catch((err) => { console.error(err); toast.error(err?.response?.data?.message || "Could not load salaries"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (!isSuperAdmin()) return <Forbidden />;

  const val = (r, f) => (edits[r.userId]?.[f] !== undefined ? edits[r.userId][f] : r[f]);
  const setVal = (r, f, v) => setEdits((e) => ({ ...e, [r.userId]: { ...(e[r.userId] || {}), [f]: v } }));
  const dirty = (r) => edits[r.userId] && Object.keys(edits[r.userId]).some((f) => String(edits[r.userId][f]) !== String(r[f]));

  const save = (r) => {
    if (!dirty(r) || saving === r.userId) return;
    const payload = {
      monthlySalary: Number(val(r, "monthlySalary")) || 0,
      currency: val(r, "currency") || "INR",
      effectiveFrom: val(r, "effectiveFrom") || "",
      notes: val(r, "notes") || ""
    };
    setSaving(r.userId);
    saveAdminCompensation(r.userId, payload)
      .then((res) => {
        const d = res?.data || {};
        // Patch just this row from the server response; other rows and their unsaved edits stay as they are.
        setRows((prev) => prev.map((x) => (x.userId === r.userId ? {
          ...x,
          monthlySalary: Number(d.monthlySalary ?? payload.monthlySalary) || 0,
          currency: d.currency || payload.currency,
          effectiveFrom: d.effectiveFrom ?? payload.effectiveFrom,
          notes: d.notes ?? payload.notes,
          updatedAt: d.updatedAt || new Date().toISOString()
        } : x)));
        setEdits((e) => { const n = { ...e }; delete n[r.userId]; return n; });
        setSavedAt((sa) => ({ ...sa, [r.userId]: Date.now() }));
        setTimeout(() => setSavedAt((sa) => { const n = { ...sa }; delete n[r.userId]; return n; }), 2500);
      })
      .catch((err) => { console.error(err); toast.error(err?.response?.data?.message || `Could not save for ${r.name}`); })
      .finally(() => setSaving(null));
  };

  const dirtyRows = rows.filter(dirty);
  const saveAll = () => dirtyRows.forEach(save);
  const onKeyDown = (r) => (e) => { if (e.key === "Enter") { e.preventDefault(); save(r); } };

  const total = rows.reduce((a, r) => a + (Number(val(r, "monthlySalary")) || 0), 0);

  return (
    <div className="intel-page">
      <div className="intel-crumbs"><Link to="/admin/intelligence">Business Intelligence</Link><span>›</span><Link to="/admin/intelligence/admins">Admin team performance</Link><span>›</span><span className="strong" style={{ color: "#3A3A3A" }}>Salary settings</span></div>
      <h1 className="intel-title">Admin salary settings <Pill tone="solid">Super admin only</Pill></h1>
      <p className="intel-subtitle">Monthly salary for each admin user (PANLS employee). Used only for the value part of the performance score and the team cost figures. Stored separately from user accounts and never shown to other profiles.</p>

      <Card title="Admin users" hint={`${rows.length} admins · total ${fmtMoney(total, "INR", false)} per month`} superOnly style={{ marginTop: 16 }}
        foot={dirtyRows.length > 0 ? <><span>{dirtyRows.length} unsaved change{dirtyRows.length > 1 ? "s" : ""}</span><button className="intel-btn small" type="button" onClick={saveAll} disabled={saving !== null}>Save all</button></> : undefined}>
        {loading && !rows.length ? <Loading text="Loading…" /> : (
          <table className="intel-table">
            <thead><tr><th>Admin</th><th>Status</th><th className="num" style={{ width: 180 }}>Monthly salary</th><th style={{ width: 110 }}>Currency</th><th style={{ width: 170 }}>Effective from</th><th>Notes</th><th style={{ width: 150 }}>Last updated</th><th style={{ width: 110 }} /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId}>
                  <td><div className="strong">{r.name}</div><div className="muted" style={{ fontSize: 11 }}>{r.emailId}{r.roleName ? ` · ${r.roleName}` : ""}</div></td>
                  <td>{r.isActive === false ? <Pill tone="grey">Inactive</Pill> : <Pill tone="green">Active</Pill>}</td>
                  <td className="num"><input type="number" min="0" step="500" placeholder="0" style={{ height: 34, width: 150, border: "1px solid #C7C7C7", borderRadius: 4, padding: "0 8px", textAlign: "right" }} value={Number(val(r, "monthlySalary")) === 0 && edits[r.userId]?.monthlySalary === undefined ? "" : val(r, "monthlySalary")} onChange={(e) => setVal(r, "monthlySalary", e.target.value)} onKeyDown={onKeyDown(r)} /></td>
                  <td><select className="intel-select" style={{ height: 34, minWidth: 90 }} value={val(r, "currency")} onChange={(e) => setVal(r, "currency", e.target.value)}><option value="INR">INR</option><option value="USD">USD</option></select></td>
                  <td><input type="date" style={{ height: 34, border: "1px solid #C7C7C7", borderRadius: 4, padding: "0 8px" }} value={val(r, "effectiveFrom") || ""} onChange={(e) => setVal(r, "effectiveFrom", e.target.value)} onKeyDown={onKeyDown(r)} /></td>
                  <td><input type="text" placeholder="Optional" style={{ height: 34, width: "100%", border: "1px solid #C7C7C7", borderRadius: 4, padding: "0 8px" }} value={val(r, "notes") || ""} onChange={(e) => setVal(r, "notes", e.target.value)} onKeyDown={onKeyDown(r)} /></td>
                  <td className="muted">{r.updatedAt ? fmtDate(r.updatedAt) : "never"}</td>
                  <td>
                    {savedAt[r.userId] ? <span className="pill green">Saved</span>
                      : <button className="intel-btn small" type="button" disabled={!dirty(r) || saving === r.userId} onClick={() => save(r)}>{saving === r.userId ? "Saving…" : "Save"}</button>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8} className="intel-empty">No admin users found.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
